import { ChevronRight } from "@mui/icons-material";
import { Button, Icon, Typography, useMediaQuery } from "@mui/material";
import { ComponentProps, ReactElement, ReactNode, useCallback } from "react";
import { FormattedMessage } from "react-intl";

import {
  CampaignStatus,
  InboxStatsForCampaign,
  InboxType,
  TexterCampaignSendStatus,
} from "~frontend/__generated__/graphql";
import { FlexBox } from "~frontend/components/FlexBox";
import { InternalLink, LinkButton } from "~frontend/components/Link";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";
import theme, { palette } from "~frontend/theme";

import { ActionButton } from "./ActionButton";
import { CampaignInboxSummary } from "./types";

export function ReplyControls({
  inboxName,
  availableCount,
  assignmentCount,
  assignmentNeedsReplyCount,
  canSendInitials,
  texterSendInProgress,
  texterSendBatchSize,
  handleEnqueueTexterSend,
  handleReleaseAssignments,
  handleRequestAssignmentBatch,
  getCampaignLink,
}: {
  inboxName: string;
  assignmentCount: number;
  availableCount: number;
  assignmentNeedsReplyCount: number;
  canSendInitials: boolean;
  handleReleaseAssignments: () => void;
  handleRequestAssignmentBatch: () => void;
  getCampaignLink: () => string;
  texterSendInProgress: boolean;
  texterSendBatchSize: number | null;
  handleEnqueueTexterSend: () => void;
}): ReactElement {
  const { subaccount } = useSubaccount()!;
  const isNarrow = useMediaQuery(theme.breakpoints.down("wide"));

  const LinkElement = ({
    children,
    to,
    onClick,
    buttonColor,
    ariaLabel,
  }: {
    children: ReactNode;
    to: string;
    onClick?: () => void;
    buttonColor?: ComponentProps<typeof ActionButton>["color"];
    ariaLabel: string;
  }) => {
    return isNarrow ? (
      <InternalLink
        variant="body1"
        sx={{
          color: palette.blue,
          textDecoration: "none",
          fontWeight: 600,
          lineHeight: "1em",
          pl: 1,
        }}
        to={to}
        onClick={onClick}
        aria-label={ariaLabel}
      >
        <FlexBox alignItems="center">
          {children}
          <Icon>
            <ChevronRight />
          </Icon>
        </FlexBox>
      </InternalLink>
    ) : (
      <ActionButton
        color={buttonColor || "primary"}
        to={to}
        onClick={onClick}
        aria-label={ariaLabel}
      >
        {children}
      </ActionButton>
    );
  };

  const goToRepliesControl = (
    <LinkElement
      to={getCampaignLink()}
      ariaLabel={
        assignmentNeedsReplyCount && !subaccount.sendingDisabled
          ? `Reply to ${assignmentNeedsReplyCount} ${inboxName || "inbox"} messages`
          : `View ${inboxName || "inbox"} threads`
      }
    >
      {assignmentNeedsReplyCount && !subaccount.sendingDisabled ? (
        <FormattedMessage
          defaultMessage="Reply ({assignmentNeedsReplyCount})"
          description="Button text for navigating to an campaign with replies"
          values={{
            assignmentNeedsReplyCount,
          }}
        />
      ) : (
        <FormattedMessage
          defaultMessage="View Threads"
          description="Button text for navigating to an campaign with replies when sending is disabled"
        />
      )}
    </LinkElement>
  );

  const releaseRepliesControl = isNarrow ? (
    <Button
      variant="contained"
      color="accessory"
      onClick={() => handleReleaseAssignments()}
      aria-label={`Release ${inboxName} Threads`}
    >
      <FormattedMessage
        defaultMessage="Release Threads"
        description="Button text for releasing campaign threads"
      />
    </Button>
  ) : (
    <LinkButton
      variant="body3"
      onClick={() => handleReleaseAssignments()}
      aria-label={`Release ${inboxName} Threads`}
    >
      <FormattedMessage
        defaultMessage="Release Threads"
        description="Button text for releasing campaign threads"
      />
    </LinkButton>
  );

  const claimRepliesButton = (
    <LinkElement
      to={getCampaignLink()}
      onClick={() => handleRequestAssignmentBatch()}
      ariaLabel={`Claim ${inboxName} Replies`}
    >
      <FormattedMessage
        defaultMessage="Claim Replies"
        description="Button text for claiming campaign replies"
      />
    </LinkElement>
  );

  if (texterSendInProgress) {
    return (
      <Typography variant="h4Caps" sx={{ color: palette.darkGray }}>
        <FormattedMessage
          defaultMessage="Sending {batchSize} Initials..."
          description="Message for when a texter campaign send is in progress"
          values={{
            // fixme: shouldn't be nullable?
            batchSize: texterSendBatchSize?.toLocaleString(),
          }}
        />
      </Typography>
    );
  }

  const sendInitialsButton = (
    <LinkElement
      buttonColor="secondary"
      to={getCampaignLink()}
      onClick={() => handleEnqueueTexterSend()}
      ariaLabel={`Send ${inboxName} initials`}
    >
      <FormattedMessage
        defaultMessage="Send initials"
        description="Text for button to send initials"
      />
    </LinkElement>
  );

  return (
    <FlexBox
      flexDirection="column"
      gap={1}
      alignItems={isNarrow ? "flex-end" : "center"}
    >
      {assignmentCount
        ? goToRepliesControl
        : availableCount
          ? claimRepliesButton
          : undefined}
      {canSendInitials ? sendInitialsButton : undefined}
      {assignmentNeedsReplyCount ? releaseRepliesControl : undefined}
    </FlexBox>
  );
}

export function CampaignInboxReplyControls({
  inbox,
  handleEnqueueTexterSend,
  handleReleaseAssignments,
  handleRequestAssignmentBatch,
  enqueuingTexterSendForCampaignId,
}: {
  inbox: CampaignInboxSummary;
  handleEnqueueTexterSend: (campaignId: string) => Promise<void>;
  handleReleaseAssignments: ({
    inboxId,
  }: {
    inboxId?: string | undefined;
  }) => Promise<void>;
  handleRequestAssignmentBatch: ({
    inboxId,
  }: {
    inboxId: string;
  }) => Promise<void>;
  enqueuingTexterSendForCampaignId: string | null;
}): ReactElement {
  const { subaccountLink } = useSubaccount()!;

  const getInboxLink = useCallback(
    ({ inboxId }: { inboxId: string }) => {
      return subaccountLink(`/inboxes/${inboxId}`);
    },
    [subaccountLink]
  );

  const canSendInitials = (inbox: CampaignInboxSummary) =>
    inbox.campaign.texterSendEnabled &&
    inbox.campaign.withinTextingHours &&
    inbox.campaign.status === CampaignStatus.InitialSendRunning;

  return (
    <ReplyControls
      inboxName={inbox.campaign.name}
      assignmentCount={inbox.stats.numAssignments}
      availableCount={inbox.stats.numRepliesAvailable}
      assignmentNeedsReplyCount={inbox.stats.numRepliesAssigned}
      canSendInitials={canSendInitials(inbox) ?? false}
      texterSendInProgress={
        enqueuingTexterSendForCampaignId === inbox.campaign.id ||
        inbox.campaign.myCampaignSends.some(
          send => send.status === TexterCampaignSendStatus.Pending
        )
      }
      getCampaignLink={() => getInboxLink({ inboxId: inbox.id })}
      texterSendBatchSize={inbox.campaign.texterSendBatchSize ?? null}
      handleEnqueueTexterSend={() => handleEnqueueTexterSend(inbox.campaign.id)}
      handleReleaseAssignments={() =>
        handleReleaseAssignments({ inboxId: inbox.id })
      }
      handleRequestAssignmentBatch={() =>
        handleRequestAssignmentBatch({ inboxId: inbox.id })
      }
    />
  );
}

export function NonCampaignInboxReplyControls({
  statsByCampaign,
  inboxType,
  inboxId,
  enqueuingTexterSendCampaignIds,
  handleEnqueueTexterSend,
  handleReleaseAssignments,
  handleRequestAssignmentBatch,
}: {
  statsByCampaign: InboxStatsForCampaign;
  inboxType: InboxType.General | InboxType.Escalation;
  inboxId: string;
  enqueuingTexterSendCampaignIds: string[];
  handleEnqueueTexterSend: (campaignId: string) => Promise<void>;
  handleReleaseAssignments: ({
    campaignId,
  }: {
    campaignId?: string | null | undefined;
  }) => Promise<void>;
  handleRequestAssignmentBatch: ({
    campaignId,
  }: {
    campaignId: string | null;
  }) => Promise<void>;
}): ReactElement {
  const { subaccountLink, subaccount } = useSubaccount()!;
  const getCampaignLink = useCallback(
    ({ campaignId }: { campaignId: string | null }) => {
      return subaccountLink(
        `/inboxes/${inboxId}/campaigns/${campaignId ?? "no_campaign"}`
      );
    },
    [subaccountLink, inboxId]
  );

  const canSendInitials =
    inboxType === InboxType.General
      ? statsByCampaign.campaign &&
        !subaccount.sendingDisabled &&
        statsByCampaign.campaign?.texterSendEnabled &&
        statsByCampaign.campaign?.status ===
          CampaignStatus.InitialSendRunning &&
        statsByCampaign.campaign?.withinTextingHours
      : false;

  const enqueuingSendForThisCampaign = enqueuingTexterSendCampaignIds.includes(
    statsByCampaign.campaign?.id ?? "null"
  );
  const hasPendingTexterSend =
    statsByCampaign.campaign?.myCampaignSends.some(
      send => send.status === TexterCampaignSendStatus.Pending
    ) ?? false;

  return (
    <ReplyControls
      inboxName={statsByCampaign.campaign?.name || "unassociated"}
      assignmentCount={statsByCampaign.assignmentCount}
      availableCount={statsByCampaign.availableCount}
      assignmentNeedsReplyCount={statsByCampaign.assignmentNeedsReplyCount}
      canSendInitials={canSendInitials ?? false}
      texterSendInProgress={
        enqueuingSendForThisCampaign || hasPendingTexterSend
      }
      getCampaignLink={() =>
        getCampaignLink({
          campaignId: statsByCampaign.campaign?.id ?? null,
        })
      }
      texterSendBatchSize={
        statsByCampaign.campaign?.texterSendBatchSize ?? null
      }
      handleEnqueueTexterSend={() =>
        handleEnqueueTexterSend(statsByCampaign.campaign!.id)
      }
      handleReleaseAssignments={() =>
        handleReleaseAssignments({
          campaignId: statsByCampaign.campaign?.id,
        })
      }
      handleRequestAssignmentBatch={() =>
        handleRequestAssignmentBatch({
          campaignId: statsByCampaign.campaign?.id ?? null,
        })
      }
    />
  );
}
